'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import Link from 'next/link';
import { FormEvent, useState } from 'react';
import { LoginLink } from '@/platform/auth/LoginLink';
import { useAuth } from '@/platform/auth/useAuth';
import { MarketplacePurchaseDisclosure } from '@/platform/commerce';
import {
  checkoutSellerCart,
  formatOrderPrice,
  removeCartItem,
  updateCartItem,
  type SellerCart,
} from '@/platform/commerce/api';

type Props = {
  sellerUserId: string;
  storeName?: string;
  cart: SellerCart | null;
  onCartChange: (cart: SellerCart | null) => void;
  onClose?: () => void;
};

export function SellerCartPanel({ sellerUserId, storeName, cart, onCartChange, onClose }: Props) {
  const { isAuthenticated } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const lineItems = cart?.lineItems ?? [];
  const currency = cart?.currency ?? 'USD';

  async function changeQty(productId: string, quantity: number) {
    if (quantity < 1) return;
    setLineLoading(productId);
    setError('');
    try {
      const result = await updateCartItem(sellerUserId, productId, { quantity });
      onCartChange(result.cart);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update cart');
    } finally {
      setLineLoading(null);
    }
  }

  async function removeLine(productId: string) {
    setLineLoading(productId);
    setError('');
    try {
      const result = await removeCartItem(sellerUserId, productId);
      onCartChange(result.cart);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove item');
    } finally {
      setLineLoading(null);
    }
  }

  async function proceedToPayment(e: FormEvent) {
    e.preventDefault();
    if (!accepted) {
      setError('Accept the marketplace purchase terms to continue.');
      return;
    }
    if (!cart || lineItems.length === 0) {
      setError('Your cart is empty.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { checkoutUrl } = await checkoutSellerCart(sellerUserId, {
        acceptedMarketplacePurchaseTerms: true,
      });
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="seller-cart-panel feed-card settings-page" aria-label="Shopping cart">
      <div className="seller-cart-panel__header">
        <h2>Cart{storeName ? ` — ${storeName}` : ''}</h2>
        {onClose && (
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        )}
      </div>

      {lineItems.length === 0 ? (
        <p className="text-muted">Your cart is empty. Add products from this store to get started.</p>
      ) : (
        <>
          <ul className="seller-cart-panel__lines">
            {lineItems.map((line) => (
              <li key={line.productId} className="seller-cart-panel__line">
                {line.imageUrl && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={line.imageUrl} alt={contentImageAlt(line.name, 'product photo')} className="cart-line__thumb" loading="lazy" />
                )}
                <div>
                  <strong>{line.name}</strong>
                  <p className="text-muted">
                    {formatOrderPrice(line.unitPriceCents, currency)} each
                  </p>
                </div>
                <div className="seller-cart-panel__line-actions">
                  <label className="marketplace-purchase__qty">
                    Qty
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={line.quantity}
                      disabled={lineLoading === line.productId}
                      onChange={(e) => {
                        const qty = Math.max(1, Number(e.target.value) || 1);
                        void changeQty(line.productId, qty);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-link"
                    disabled={lineLoading === line.productId}
                    onClick={() => void removeLine(line.productId)}
                  >
                    Remove
                  </button>
                </div>
                <p>{formatOrderPrice(line.unitPriceCents * line.quantity, currency)}</p>
              </li>
            ))}
          </ul>
          <p>
            <strong>Subtotal: {formatOrderPrice(cart?.subtotalCents ?? 0, currency)}</strong>
          </p>
          <p className="text-muted vendor-console__hint">
            Shipping options and tax are calculated at secure Stripe checkout.
          </p>

          <form onSubmit={(e) => void proceedToPayment(e)}>
            <MarketplacePurchaseDisclosure storeName={storeName} accepted={accepted} onAcceptedChange={setAccepted} />
            {error && <p className="form-error">{error}</p>}
            <div className="seller-cart-panel__checkout-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Redirecting…' : 'Proceed to payment'}
              </button>
              {!isAuthenticated && (
                <p className="text-muted">
                  <LoginLink returnTo={`/marketplace/sellers/${sellerUserId}/?cart=1`}>
                    Sign in to save order history
                  </LoginLink>
                  {' · '}Guest checkout uses Stripe to collect your email.
                </p>
              )}
            </div>
          </form>
        </>
      )}

      <p className="text-muted">
        <Link href={`/marketplace/sellers/${sellerUserId}/`}>Continue shopping</Link>
      </p>
    </section>
  );
}
